import React, { useState } from 'react';
import { getCookie, setCookie } from '../utils/cookies';
import { HeaderLangGroupProps } from '../../base/interfaces/headerInterface';
import { LanguageDropdownStyles } from '../styles/languageDropdown';
import { MenuList, MenuItem, ListItemText, useTheme, useMediaQuery } from '@material-ui/core';
import { reValidateSession } from '../../authentication/utils/revalidateSession';
import { getValidLanguage } from '../../authentication/utils/language';
import { ReactComponent as CompletedTaskIcon } from '../../taskList/images/completed_task.svg';
import { useAppSelector } from '../../redux-store/hook';

/** Language Dropdown Props */
interface LanguageDropdownProps {
  languages: ReadonlyArray<HeaderLangGroupProps> | undefined;
  onLanguageChange: () => void;
}
/** Language Dropdown Component */
const StateLessLanguageDropdown = (props: LanguageDropdownProps): JSX.Element => {
  const { languages } = props;
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const template = useAppSelector((state) => state.template);
  const { settings } = template;

  const classes = LanguageDropdownStyles(settings);

  const [selectedLanguage, setSelectedLanguage] = useState(getCookie('locale_player') || 'EN_US');

  /** Language Change Handler */
  const onLanguageChange = (locale: string) => async (event: React.MouseEvent | React.KeyboardEvent): Promise<void> => {
    /** todo This is a work around for Ukrainian language to be supported,
    it should handled on database localization and UI localization, having the same locale 'uk_UA' */
    const validLanguage = getValidLanguage(locale);

    if (event.type === 'keydown') {
      return;
    }
    if (
      event.type === 'keyup' &&
      !((event as React.KeyboardEvent).key === 'Enter' || (event as React.KeyboardEvent).key === 'Space')
    ) {
      return;
    }
    event.preventDefault();
    if (getCookie('locale_player') !== locale) {
      setSelectedLanguage(validLanguage);
      setCookie('locale_player', validLanguage);
      setCookie('oldlang', validLanguage);
      await reValidateSession(template, 'fetchTemplate=true');
      props.onLanguageChange();
      window.location.reload();
    }
  };

  const handleSettingsKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Tab') {
      event.preventDefault();
      props.onLanguageChange();
    }
  };

  return (
    <MenuList className={classes.languageList} aria-label="Languages List" onKeyDown={handleSettingsKeyDown}>
      {languages &&
        languages.map((language) => (
          <MenuItem
            button
            className={!lgUp ? classes.listItem : ''}
            tabIndex={0}
            role="button"
            key={`${language.languageId}_${language.locale}`}
            selected={selectedLanguage === language.locale}
            onClick={onLanguageChange(language.locale)}
            onKeyUp={onLanguageChange(language.locale)}>
            <ListItemText className={classes.fontSize} primary={language.languageName} />

            {selectedLanguage === language.locale && (
              <CompletedTaskIcon width={15} height={15} className={classes.icon} />
            )}
          </MenuItem>
        ))}
    </MenuList>
  );
};

export const LanguageDropdown = React.memo(StateLessLanguageDropdown);
